import { 
  MessageSquare, 
  Users, 
  BarChart3, 
  Shield, 
  Infinity,
  Headphones,
  Sparkles,
  Bot,
  Calendar,
  Kanban,
  FileText,
  Workflow
} from "lucide-react";
import { motion } from "framer-motion";

const features = [
  {
    icon: Shield,
    title: "API Oficial sem complicações",
    description: "Conecte-se à WhatsApp Business API em minutos, com login incorporado e templates aprovados. Profissionalismo de verdade no seu atendimento.",
    color: "purple" as const,
  },
  {
    icon: FileText,
    title: "Templates Oficiais",
    description: "Crie e envie conversas com templates aprovados pela Meta. Inicie atendimentos a qualquer momento, com a janela de 24h sob controle.",
    color: "cyan" as const,
  },
  {
    icon: Sparkles,
    title: "Reescrita com IA",
    description: "Ajuste o tom da mensagem (profissional, simpático ou marketing) em um clique, sem sair da conversa.",
    color: "purple" as const,
  },
  {
    icon: Bot,
    title: "Chatbots com IA integrada",
    description: "Conecte ChatGPT, Claude e Gemini ao seu fluxo. Qualifique leads, responda dúvidas e transfira para humanos quando necessário.",
    color: "cyan" as const,
  },
  {
    icon: MessageSquare,
    title: "Omnichannel real",
    description: "WhatsApp, Instagram, Facebook, Telegram, Webchat e E-mail em uma única caixa de entrada. Nunca mais perca um lead.",
    color: "purple" as const,
  },
  {
    icon: Workflow,
    title: "Filas e setores inteligentes",
    description: "Organize por Vendas, Suporte ou Financeiro com distribuição automática, transferência entre agentes e SLA monitorado.",
    color: "cyan" as const,
  },
  {
    icon: Infinity,
    title: "Conexões e equipe ilimitadas",
    description: "Adicione quantos números e atendentes precisar. Preço fixo, sem cobrança por assento ou por conexão.",
    color: "purple" as const,
  },
  {
    icon: Kanban,
    title: "CRM + Kanban de vendas",
    description: "Funil de oportunidades visual, valor negociado por atendimento e ações automáticas ao mover entre etapas.",
    color: "cyan" as const,
  },
  {
    icon: Calendar,
    title: "Agendamento e campanhas",
    description: "Programe envios individuais ou em massa para a data e hora exatas. Segmente por etiqueta e personalize com variáveis.",
    color: "purple" as const,
  },
  {
    icon: Users,
    title: "Multiatendimento simultâneo",
    description: "Vários atendentes no mesmo número ao mesmo tempo, com fila inteligente e transferência sem perder o contexto.",
    color: "cyan" as const,
  },
  {
    icon: BarChart3,
    title: "Relatórios em tempo real",
    description: "Tempo de resposta, NPS, taxa de resolução e desempenho por agente — tudo em dashboards exportáveis.",
    color: "purple" as const,
  },
  {
    icon: Headphones,
    title: "Suporte brasileiro",
    description: "Time de suporte humano em português, com SLA prioritário no plano Elite. Sem chatbot genérico em inglês.",
    color: "cyan" as const,
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 },
  },
};

const FeaturesSection = () => {
  return (
    <section id="recursos" className="py-16 md:py-24 relative" aria-labelledby="features-heading">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-surface-card/50 to-transparent" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-3xl mx-auto mb-12 md:mb-16"
        >
          <h2 id="features-heading" className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-display font-bold mb-4 md:mb-6">
            Tudo para{" "}
            <span className="text-gradient-zavvi">escalar seu atendimento</span>
          </h2>
          <p className="text-base md:text-lg text-body-text px-4">
            Da API Oficial do WhatsApp à automação com IA: uma plataforma completa, sem limites artificiais.
          </p>
        </motion.div>

        {/* Features Grid */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6"
        >
          {features.map((feature) => (
            <motion.div
              key={feature.title}
              variants={itemVariants}
              whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
              className={`glass-card p-5 md:p-6 rounded-2xl transition-all duration-300 group ${
                feature.color === "cyan" ? "hover:neon-border-cyan" : "hover:neon-border-purple"
              }`}
            >
              <div
                className={`w-10 md:w-12 h-10 md:h-12 rounded-xl flex items-center justify-center mb-3 md:mb-4 transition-all duration-300 ${
                  feature.color === "cyan"
                    ? "bg-cyber-cyan/10 text-cyber-cyan group-hover:glow-cyan"
                    : "bg-electric-purple/10 text-electric-purple group-hover:glow-purple"
                }`}
              >
                <feature.icon size={20} className="md:w-6 md:h-6" />
              </div>
              <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3 text-foreground">{feature.title}</h3>
              <p className="text-sm md:text-base text-body-text">{feature.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturesSection;
